import { GetCategoriesIcon, GetSportIcon, TvIcon } from 'assets/icons';
import ArrowIcon from 'assets/images/arrows/arrowIconThick.svg?react';
import cn from 'classnames';
import { OddsType } from 'features/eventLive/interface/interface';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import CardAdditionalBet from 'shared/components/eventCardHome/component/cardAdditionalBet';
import OddsExpander from 'shared/components/eventCardHome/component/oddsExpander';
import LiveBadge from 'shared/components/liveBadge';
import Odds from 'shared/components/odds/container';
import { ServingTeamIcon } from 'shared/components/servingTeamIcon';
import { APP_URL, LiveMatchStatus } from 'shared/constants/common';
import { BETSType, CouponState, EventType, State } from 'shared/interface';
import { addBet } from 'shared/store/coupon.action';
import { eventDateDisplay } from 'shared/util/createDate';
import { isEsport, isMobile, removeMultipleDash, removeSpecialCharacter, setAllBets } from 'shared/util/utility';
import LiveScore from './component/liveScore';

import { useEventStartFiltersConsts } from 'shared/hooks/useEventStartFilters';
import './styles.scss';

interface EventCardHomeProps {
	className?: string;
	isLive?: boolean;
	isTwitch?: boolean;
	isStatScore?: boolean;
	sportID: number;
	categoryId: number;
	parentId: number;
	infos?: string;
	eventStart: number;
	partialScores?: string[];
	gamesCount: number;
	participants: string[];
	event: any;
	sportEventImg?: string | null;
	showBoldNumber?: boolean;
	forceDisableNoFollow?: boolean;
	sliderTab?: string;
}

export const EventCardHome = memo(({ forceDisableNoFollow, sliderTab, ...props }: EventCardHomeProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const { isLive, sportID, categoryId, eventStart, gamesCount, participants, parentId, event } = props;

	const [areBetsExpanded, setAreBetsExpanded] = useState(false);
	const [areOddsExpanded, setAreOddsExpanded] = useState(false);
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const analytics = useSelector((state: State) => state.coupon.analytics);

	const oddsComparison = useSelector<State, OddsType[]>((state) => state.home.oddsComparison);

	const returnCheck = (id: number) => {
		const outcome: any = oddsComparison ? oddsComparison.find((item: OddsType) => item?.outcomeId === id) || { isUpdate: null } : [];
		return outcome.isUpdate;
	};

	const onClickOdds = (item: any) => {
		const betObj = {
			outcomeId: item.outcomeId,
			outcomeOdds: item.outcomeOdds,
			outcomeName: item.outcomeName,
			eventStart: item.eventStart,
			sportId: sportID,
			eventId: item.eventId,
			eventType: isLive ? EventType.LIVE : EventType.PREMATCH,
			gameName: item.gameName,
			gameType: item.gameType,
			categoryId: item.categoryId,
			gameId: item.gameId,
			eventName: item.eventName,
			league: item.league,
			selectedBet: true,
			banker: false,
			isLive: !!isLive,
			combinationType: 1,
		};

		dispatch(addBet({ ...bets, ...setAllBets(bets, betObj, dispatch, coupon, analytics, t) }));
	};

	const participantUrl = () => {
		if (participants.length === 1) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return removeSpecialCharacter(participants[0]!);
		}
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return removeMultipleDash(`${removeSpecialCharacter(participants[0]!)}-${removeSpecialCharacter(participants[1]!)}`);
	};

	const eventUrl = () => {
		let URL = '';
		if (isLive) {
			URL = `${APP_URL.live}/${removeSpecialCharacter(event.categoryName)}/${participantUrl()}-live/${event.eventId}`;
		} else {
			if (isEsport(event)) {
				URL = `${APP_URL.eSport}/${removeSpecialCharacter(event.sportName)}/${removeSpecialCharacter(
					event.parentName,
				)}/${removeSpecialCharacter(event.categoryName)}/${participantUrl()}/${event.eventId}`;
			} else {
				URL = `${APP_URL.sport}/${removeSpecialCharacter(event.sportName)}/${removeSpecialCharacter(
					event.parentName,
				)}/${removeSpecialCharacter(event.categoryName)}/${participantUrl()}/${event.eventId}`;
			}
		}

		if (sliderTab) {
			URL += `?${useEventStartFiltersConsts.EVENT_START_FILTER_SEARCH_PARAM}=${sliderTab}`;
		}

		return removeMultipleDash(URL);
	};

	const participant1 = participants[0];
	const participant2 = participants[1];

	const isStreamIcon =
		(event.providerId === 10 && event.liveMatchStatus === LiveMatchStatus.SIS_IN_PLAY) ||
		(event.providerId !== 10 && !!event.extras?.betradarLivestreamLCO) ||
		event.extras?.twitchChannel;

	const handleExpand = (e: any) => {
		e.preventDefault();
		if (isMultipleOdds) {
			setAreOddsExpanded(!areOddsExpanded);
		}
		if (areBetsExpandable) {
			setAreBetsExpanded(!areBetsExpanded);
		}
	};

	const excludedGameTypes = [1]; //exluce 1x2 game since its there already

	const eventArrayWithoutFirstGameType = (event: any) => {
		if (event.games.length > 1) {
			return event.games.slice(1).filter((item: any) => !excludedGameTypes.includes(item.gameType));
		} else return event.games;
	};

	const areBetsExpandable = event.games?.length > 1 && !isLive; // checks if the event has more than 1 gameType to show
	const isSingleParticipant = participants.length === 1;
	const isMultipleOdds = event?.outcomes?.length > 3;
	const desktopExpandConditions = areBetsExpanded ? areBetsExpanded : areOddsExpanded;
	const isEventExpandable = areBetsExpandable ? areBetsExpandable : isMultipleOdds;
	return (
		<>
			<Link
				to={eventUrl()}
				className={cn('eventCardHome', { 'eventCardHome--expanded': areBetsExpanded })}
				rel={event.disableNoFollow || forceDisableNoFollow ? undefined : 'nofollow'}
			>
				<div className={cn('eventCardHome__card', { 'eventCardHome__card--withImage': !!props.sportEventImg })}>
					{!!props.sportEventImg && (
						<img
							className="eventCardHome__info__image "
							src={props.sportEventImg}
							alt={event.imageAltText || event.name}
							loading="lazy"
						/>
					)}
					<div className="eventCardHome__card__wrapper">
						<div className="eventCardHome__info">
							<div className="eventCardHome__info__icons">
								<div className="eventCardHome__info__icons__wrap">
									{isLive !== undefined && event.isLive && <LiveBadge />}
									{!!isStreamIcon && (
										<div className="eventCardHome__info__icons__stream">
											<TvIcon />
										</div>
									)}
									<GetSportIcon id={sportID} />
									<GetCategoriesIcon
										category={{
											sportId: sportID,
											parentCategory: categoryId,
											categoryId: parentId,
										}}
									/>
									<span>{event.categoryName}</span>
								</div>
								{gamesCount > 0 && <div className="eventCardHome__info__icons__eventCount">+{gamesCount}</div>}
							</div>
							<div
								className={cn('eventCardHome__info__participants', {
									'eventCardHome__info__participants--single': isSingleParticipant,
									'eventCardHome__info__participants--double': !isSingleParticipant,
								})}
							>
								{isSingleParticipant ? (
									<>
										<div className="eventCardHome__info__participants__time">{eventDateDisplay(eventStart)}</div>
										<div className="eventCardHome__info__participants__participant">
											<span>{participant1}</span>
										</div>
									</>
								) : (
									<>
										<div className="eventCardHome__info__participants__participant">
											{event.servingTeam === 1 && <ServingTeamIcon sportId={event.sportId} />}
											<span className="">{participant1}</span>
										</div>
										{isLive ? (
											<LiveScore event={event} />
										) : (
											<div className="eventCardHome__info__participants__time">{eventDateDisplay(eventStart)}</div>
										)}
										<div className="eventCardHome__info__participants__participant">
											<span>{participant2}</span>
											{event.servingTeam === 2 && <ServingTeamIcon sportId={event.sportId} />}
										</div>
									</>
								)}
							</div>
						</div>

						<div className="eventCardHome__odds">
							<Odds eventUrl={eventUrl} onClickOdds={onClickOdds} returnCheck={returnCheck} data={event} expandOdds={areOddsExpanded} />

							{!isMobile() && isEventExpandable && (
								<button
									className={cn(
										'eventCardHome__expand',
										{ 'eventCardHome__expand--inactive': !desktopExpandConditions },
										{ 'eventCardHome__expand--active': desktopExpandConditions },
									)}
									onClick={handleExpand}
									type="button"
								>
									<div
										className={cn('eventCardHome__expand__icon', {
											'eventCardHome__expand__icon--expanded': desktopExpandConditions,
										})}
									>
										<ArrowIcon />
									</div>
								</button>
							)}
						</div>
					</div>
				</div>
				<AnimatePresence>
					{areBetsExpanded && (
						<motion.div
							className="eventCardHome__additionalBets"
							initial={{ height: 0 }}
							animate={{ height: 'auto' }}
							exit={{ height: 0 }}
							transition={{ duration: 0.3 }}
						>
							{eventArrayWithoutFirstGameType(event).map((item: any, index: number) => (
								<CardAdditionalBet
									eventUrl={eventUrl}
									event={event}
									onClickOdds={onClickOdds}
									key={`additional-bet-${index}`}
									data={item}
								/>
							))}
						</motion.div>
					)}
				</AnimatePresence>
				{isMobile() && (
					<OddsExpander
						gamesCount={gamesCount}
						eventUrl={eventUrl}
						isEventExpandable={isEventExpandable}
						isExpanded={areBetsExpanded ? areBetsExpanded : areOddsExpanded}
						handleExpand={handleExpand}
					/>
				)}
			</Link>
		</>
	);
});

EventCardHome.displayName = 'EventCardHome';
